import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'umi';
import { getName } from '@home/utils/common';
import SvgIcon from '@home/components/SvgIcon';
import BasicImage from '@home/components/BasicImage';
import DatabarinSvg from '@/pages/home/assets/svg/dataSource/databarin.svg';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { LANGUAGE_EN } from '@home/_config/config';
import Loading from '@home/components/Loading';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { dynamicString } from '@home/utils/dynamicString';
import { getValueFromData } from '@/pages/benchmark/utils/utils';
import classNames from 'classnames';
import type {
  IKeyMetricBenchmarkDataItem,
  IKeyMetricBenchmarkFooterConfig,
  IKeyMetricBenchmarkMetricItem,
  IKeyMetricBenchmarkPanelProps,
  IKeyMetricBenchmarkSection,
  IKeyMetricBenchmarkServiceState,
  IKeyMetricBenchmarkTableColumn,
  IKeyMetricBenchmarkTextValueJson,
  TKeyMetricBenchmarkAlign,
} from './types';
import './index.scss';

const EMPTY_VALUE = '-';
const DEFAULT_VALUE_PATH = '{{median}}';
const DEFAULT_LABEL_ALIGN: TKeyMetricBenchmarkAlign = 'left';
const DEFAULT_VALUE_ALIGN: TKeyMetricBenchmarkAlign = 'right';
const DEFAULT_LABEL_COL_WIDTH = '80px';
const DEFAULT_VALUE_COL_WIDTH = '52px';

const resolveColumnSize = (
  width: string | number | undefined,
  fallback: string,
): string => {
  if (width === undefined || width === null || width === '') {
    return fallback;
  }
  if (typeof width === 'number') {
    return `${width}px`;
  }
  return width;
};

/** 配置宽度为最小值，内容超出时按 max-content 撑开 */
const formatColumnWidth = (
  width: string | number | undefined,
  fallback: string,
): string => {
  const resolved = resolveColumnSize(width, fallback);
  return `minmax(${resolved}, max-content)`;
};

const hasMetricValueCell = (
  metric: IKeyMetricBenchmarkMetricItem,
  tableColumnKey: string,
): boolean =>
  tableColumnKey in (metric.valueKeys || {}) ||
  tableColumnKey in (metric.override || {});

const hasValueKeyMapping = (
  tableColumn: IKeyMetricBenchmarkTableColumn,
  section: IKeyMetricBenchmarkSection,
): boolean =>
  section.metrics?.some(metric => hasMetricValueCell(metric, tableColumn.key)) ??
  false;

const getTableColumns = (section: IKeyMetricBenchmarkSection) =>
  section.columns || [];

const getCellsPerSection = (section: IKeyMetricBenchmarkSection): number =>
  getTableColumns(section).length;

const getSectionColStart = (
  sections: IKeyMetricBenchmarkSection[],
  sectionIndex: number,
): number => {
  let start = 1;
  for (let i = 0;i < sectionIndex;i++) {
    start += getCellsPerSection(sections[i]);
  }
  return start;
};

const resolveColumnWidth = (
  tableColumn: IKeyMetricBenchmarkTableColumn,
  isEn: boolean,
): string | number | undefined =>
  isEn
    ? tableColumn.enWidth ?? tableColumn.width
    : tableColumn.cnWidth ?? tableColumn.width;

const buildSectionColumnWidths = (
  section: IKeyMetricBenchmarkSection,
  isEn: boolean,
): string =>
  getTableColumns(section)
    .map(tableColumn =>
      formatColumnWidth(
        resolveColumnWidth(tableColumn, isEn),
        hasValueKeyMapping(tableColumn, section)
          ? DEFAULT_VALUE_COL_WIDTH
          : DEFAULT_LABEL_COL_WIDTH,
      ),
    )
    .join(' ');

const getAlignClassName = (align: TKeyMetricBenchmarkAlign = 'left'): string =>
  `dbd-key-metric-benchmark-panel-cell--${align}`;

const resolveTableColumnAlign = (
  tableColumn: IKeyMetricBenchmarkTableColumn,
  section: IKeyMetricBenchmarkSection,
): TKeyMetricBenchmarkAlign => {
  if (tableColumn.align) {
    return tableColumn.align;
  }
  return hasValueKeyMapping(tableColumn, section)
    ? DEFAULT_VALUE_ALIGN
    : DEFAULT_LABEL_ALIGN;
};

const resolveValuePath = (
  metric: IKeyMetricBenchmarkMetricItem,
  tableColumnKey: string,
  panelValuePath?: string,
): string =>
  metric.valuePaths?.[tableColumnKey] ||
  metric.valuePath ||
  panelValuePath ||
  DEFAULT_VALUE_PATH;

const parseTextValueJson = (
  textValueJson?: IKeyMetricBenchmarkTextValueJson | string | null,
): IKeyMetricBenchmarkTextValueJson | undefined => {
  if (!textValueJson) {
    return undefined;
  }
  if (typeof textValueJson === 'string') {
    try {
      const parsed = JSON.parse(textValueJson);
      return typeof parsed === 'object' && parsed !== null
        ? (parsed as IKeyMetricBenchmarkTextValueJson)
        : undefined;
    } catch {
      return undefined;
    }
  }
  return textValueJson;
};

const normalizeBenchmarkDataItem = (
  dataItem?: IKeyMetricBenchmarkDataItem,
): IKeyMetricBenchmarkDataItem | undefined => {
  if (!dataItem) {
    return undefined;
  }

  const parsedTextValueJson = parseTextValueJson(dataItem.text_value_json);
  if (!parsedTextValueJson) {
    return dataItem;
  }

  return {
    ...dataItem,
    text_value_json: parsedTextValueJson,
  };
};

const extractDynamicPath = (valuePath: string): string | undefined => {
  const match = valuePath.match(/\{\{([^}|]+)\}\}/);
  return match?.[1]?.trim();
};

const hasInlineFormat = (valuePath: string): boolean =>
  valuePath.includes('|dataFormat');

const resolveBenchmarkValue = (
  dataItem: IKeyMetricBenchmarkDataItem | undefined,
  valuePath: string,
): string | number | undefined => {
  const normalized = normalizeBenchmarkDataItem(dataItem);
  if (!normalized) {
    return undefined;
  }

  const dynamicPath = extractDynamicPath(valuePath);
  let resolved = dynamicString(valuePath, normalized, '');

  if (resolved === '' && dynamicPath) {
    const dataKey = dynamicPath.split('.').pop() || dynamicPath;
    const fallbackValue = getValueFromData(normalized, dataKey);
    if (
      fallbackValue !== undefined &&
      fallbackValue !== null &&
      fallbackValue !== ''
    ) {
      resolved = String(fallbackValue);
    }
  }

  return resolved === '' ? undefined : resolved;
};

const shouldSkipFormat = (
  metric: IKeyMetricBenchmarkMetricItem,
  valuePath: string,
): boolean => metric.skipFormat === true || hasInlineFormat(valuePath);

const getMetricValue = (
  metric: IKeyMetricBenchmarkMetricItem,
  tableColumnKey: string,
  benchmarkData: IKeyMetricBenchmarkPanelProps['benchmarkData'],
  panelValuePath?: string,
): { value: string | number | undefined; skipFormat: boolean; } => {
  const overrideValue = metric.override?.[tableColumnKey];
  if (overrideValue !== undefined && overrideValue !== '') {
    return { value: overrideValue, skipFormat: metric.skipFormat === true };
  }

  const metricKey = metric.valueKeys?.[tableColumnKey];
  if (!metricKey) {
    return { value: undefined, skipFormat: false };
  }

  const dataItem = benchmarkData?.[metricKey];
  const valuePath = resolveValuePath(metric, tableColumnKey, panelValuePath);
  return {
    value: resolveBenchmarkValue(dataItem, valuePath),
    skipFormat: shouldSkipFormat(metric, valuePath),
  };
};

const formatMetricValue = (
  value: string | number | undefined,
  formatRule?: string,
  skipFormat = false,
): string => {
  if (value === undefined || value === null || value === '') {
    return EMPTY_VALUE;
  }
  if (!formatRule || skipFormat) {
    return String(value);
  }
  return String(dataFormat({ value, formatRule }));
};

const getSectionKey = (
  section: IKeyMetricBenchmarkSection,
  sectionIndex: number,
) => getTableColumns(section)[0]?.key || `section-${sectionIndex}`;

const renderFooter = (
  footerConfig: IKeyMetricBenchmarkFooterConfig | undefined,
  isEn: boolean,
  fallbackMessage?: string,
) => {
  const disclaimer = getName(
    isEn,
    footerConfig?.disclaimerEn,
    footerConfig?.disclaimerCn,
  );
  const contactPrefix = getName(
    isEn,
    footerConfig?.contactPrefixEn,
    footerConfig?.contactPrefixCn,
  );
  const contactEmail = footerConfig?.contactEmail;

  if (disclaimer || (contactPrefix && contactEmail)) {
    return (
      <div className="dbd-key-metric-benchmark-panel-footer">
        {disclaimer && (
          <div className="dbd-key-metric-benchmark-panel-footer-line">
            {disclaimer}
          </div>
        )}
        {contactPrefix && contactEmail && (
          <div className="dbd-key-metric-benchmark-panel-footer-line">
            {contactPrefix}
            <a
              className="dbd-key-metric-benchmark-panel-footer-email"
              href={`mailto:${contactEmail}`}
            >
              {contactEmail}
            </a>
          </div>
        )}
      </div>
    );
  }

  if (!fallbackMessage) {
    return null;
  }

  return (
    <div className="dbd-key-metric-benchmark-panel-footer">
      <div className="dbd-key-metric-benchmark-panel-footer-line">
        {fallbackMessage}
      </div>
    </div>
  );
};

export default function KeyMetricBenchmarkPanel(
  props: IKeyMetricBenchmarkPanelProps,
) {
  const {
    sections = [],
    data,
    dataKey = 'keyMetricBenchmarkData',
    benchmarkData: benchmarkDataProp,
    valuePath = DEFAULT_VALUE_PATH,
    titleEn = 'Benchmark (Median)',
    titleCn = '行业基准 (中位数)',
    moreEn = 'More',
    moreCn = '更多',
    jumpUrl,
    footerConfig,
    bottomMessageEn,
    bottomMessageCn,
    loading: loadingProp,
    logConfig,
  } = props;

  const benchmarkState = (
    dataKey ? data?.[dataKey] : undefined
  ) as IKeyMetricBenchmarkServiceState | undefined;
  const benchmarkData = benchmarkDataProp ?? benchmarkState?.data;
  const loading = loadingProp ?? benchmarkState?.loading;

  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const title = getName(isEn, titleEn, titleCn);
  const moreText = getName(isEn, moreEn, moreCn);
  const fallbackBottomMessage = getName(isEn, bottomMessageEn, bottomMessageCn);

  const gridTemplateColumns = useMemo(
    () =>
      sections.map(section => buildSectionColumnWidths(section, isEn)).join(' '),
    [sections, isEn],
  );

  const maxMetricCount = useMemo(
    () =>
      Math.max(...sections.map(section => section.metrics?.length || 0), 0),
    [sections],
  );

  const jumpToLink = useCallback(
    (url: string, linkTitle: string) => {
      if (url) {
        window.open(url);
      }
      buriedBtnPoint({
        typeId: 'dashboard_pc_v2',
        ...logConfig,
        pageId: logConfig?.pageId ?? '',
        buttonId: logConfig?.buttonId ?? '',
        extInfo: linkTitle,
      });
    },
    [logConfig],
  );

  const renderCell = (
    sectionIndex: number,
    cellOffset: number,
    className: string,
    content: React.ReactNode,
    options?: {
      gridRow?: number;
      isSectionEnd?: boolean;
      isSectionStart?: boolean;
    },
  ) => {
    const colStart = getSectionColStart(sections, sectionIndex) + cellOffset;
    return (
      <div
        className={classNames('dbd-key-metric-benchmark-panel-cell', className, {
          'dbd-key-metric-benchmark-panel-cell--group-end':
            options?.isSectionEnd,
          'dbd-key-metric-benchmark-panel-cell--group-start':
            options?.isSectionStart,
        })}
        style={{
          gridColumn: colStart,
          gridRow: options?.gridRow,
        }}
      >
        {content}
      </div>
    );
  };

  const renderHeaderCells = (
    section: IKeyMetricBenchmarkSection,
    sectionIndex: number,
  ) => {
    const isSectionEnd = sectionIndex < sections.length - 1;
    const tableColumns = getTableColumns(section);
    const sectionKey = getSectionKey(section, sectionIndex);

    return (
      <React.Fragment key={`header-${sectionKey}-${sectionIndex}`}>
        {tableColumns.map((tableColumn, tableColumnIndex) => {
          const isLastTableColumn = tableColumnIndex === tableColumns.length - 1;
          const isFirstTableColumn = tableColumnIndex === 0;
          return renderCell(
            sectionIndex,
            tableColumnIndex,
            classNames(
              'dbd-key-metric-benchmark-panel-cell--header',
              getAlignClassName(resolveTableColumnAlign(tableColumn, section)),
            ),
            getName(
              isEn,
              dynamicString(
                tableColumn.titleEn ?? tableColumn.nameEn ?? '',
                data,
              ),
              dynamicString(
                tableColumn.titleCn ?? tableColumn.nameCn ?? '',
                data,
              ),
            ),
            {
              gridRow: 1,
              isSectionEnd: isSectionEnd && isLastTableColumn,
              isSectionStart: sectionIndex > 0 && isFirstTableColumn,
            },
          );
        })}
      </React.Fragment>
    );
  };

  const renderDataCellContent = (
    metric: IKeyMetricBenchmarkMetricItem,
    tableColumn: IKeyMetricBenchmarkTableColumn,
  ) => {
    if (!hasMetricValueCell(metric, tableColumn.key)) {
      return getName(
        isEn,
        dynamicString(metric.nameEn ?? metric.enName ?? '', data),
        dynamicString(metric.nameCn ?? metric.cnName ?? '', data),
      );
    }
    const { value, skipFormat } = getMetricValue(
      metric,
      tableColumn.key,
      benchmarkData,
      valuePath,
    );
    const formatter = metric.formatRule ?? metric.formatter;
    return formatMetricValue(
      value,
      formatter ? dynamicString(formatter, data) : undefined,
      skipFormat,
    );
  };

  const renderDataCells = (
    section: IKeyMetricBenchmarkSection,
    sectionIndex: number,
    rowIndex: number,
  ) => {
    const metric = section.metrics[rowIndex];
    const isSectionEnd = sectionIndex < sections.length - 1;
    const gridRow = 2 + rowIndex;
    const tableColumns = getTableColumns(section);
    const sectionKey = getSectionKey(section, sectionIndex);

    if (!metric) {
      return (
        <React.Fragment key={`empty-${sectionKey}-${rowIndex}`}>
          {tableColumns.map((tableColumn, tableColumnIndex) => {
            const isLastTableColumn =
              tableColumnIndex === tableColumns.length - 1;
            const isFirstTableColumn = tableColumnIndex === 0;
            return renderCell(
              sectionIndex,
              tableColumnIndex,
              'dbd-key-metric-benchmark-panel-cell--data',
              null,
              {
                gridRow,
                isSectionEnd: isSectionEnd && isLastTableColumn,
                isSectionStart: sectionIndex > 0 && isFirstTableColumn,
              },
            );
          })}
        </React.Fragment>
      );
    }

    const cellStyle = { gridRow };

    return (
      <React.Fragment key={`${metric.nameEn}-${rowIndex}`}>
        {tableColumns.map((tableColumn, tableColumnIndex) => {
          const isLastTableColumn = tableColumnIndex === tableColumns.length - 1;
          const isFirstTableColumn = tableColumnIndex === 0;
          const isValueColumn = hasValueKeyMapping(tableColumn, section);

          return (
            <div
              key={`${metric.nameEn}-${tableColumn.key}-${rowIndex}`}
              className={classNames(
                'dbd-key-metric-benchmark-panel-cell',
                'dbd-key-metric-benchmark-panel-cell--data',
                isValueColumn
                  ? 'dbd-key-metric-benchmark-panel-cell--value'
                  : 'dbd-key-metric-benchmark-panel-cell--metric-name',
                getAlignClassName(resolveTableColumnAlign(tableColumn, section)),
                {
                  'dbd-key-metric-benchmark-panel-cell--group-end':
                    isSectionEnd && isLastTableColumn,
                  'dbd-key-metric-benchmark-panel-cell--group-start':
                    sectionIndex > 0 && isFirstTableColumn,
                },
              )}
              style={{
                ...cellStyle,
                gridColumn:
                  getSectionColStart(sections, sectionIndex) + tableColumnIndex,
              }}
            >
              {renderDataCellContent(metric, tableColumn)}
            </div>
          );
        })}
      </React.Fragment>
    );
  };

  return (
    <div
      className={classNames('dbd-key-metric-benchmark-panel', {
        'dbd-key-metric-benchmark-panel-loading': loading,
      })}
      onClick={e => e.stopPropagation()}
    >
      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="dbd-key-metric-benchmark-panel-header">
            <div className="dbd-key-metric-benchmark-panel-title">
              <div className="dbd-key-metric-benchmark-panel-title-text">
                <BasicImage src={DatabarinSvg} />
                {title}
              </div>
              {jumpUrl && (
                <div
                  className="dbd-key-metric-benchmark-panel-title-more"
                  onClick={() => jumpToLink(jumpUrl, title)}
                >
                  {moreText} <SvgIcon type="link-to" />
                </div>
              )}
            </div>
          </div>
          <div className="dbd-key-metric-benchmark-panel-body">
            <div
              className="dbd-key-metric-benchmark-panel-table"
              style={{ gridTemplateColumns }}
            >
              {sections.map((section, sectionIndex) =>
                renderHeaderCells(section, sectionIndex),
              )}
              {Array.from({ length: maxMetricCount }).map((_, rowIndex) =>
                sections.map((section, sectionIndex) =>
                  renderDataCells(section, sectionIndex, rowIndex),
                ),
              )}
            </div>
          </div>
          {renderFooter(footerConfig, isEn, fallbackBottomMessage)}
        </>
      )}
    </div>
  );
}
